/**
 * Breadcrumbs — renders the trail computed by `useCrumbs` (journey-aware
 * with canonical fallback; see lib/use-crumbs.ts for the derivation rules).
 *
 * Two variants:
 *   - full (default): the classic Home → … → current trail for md+ viewports.
 *   - compact: a single "‹ parent" back link for small viewports, where the
 *     full trail doesn't fit but users still need a way up the hierarchy.
 */
import { Fragment } from 'react';
import { Link } from '@tanstack/react-router';
import { ChevronLeft, ChevronRight, Home } from 'lucide-react';
import { useCrumbs, type BreadcrumbItem } from '@/lib/use-crumbs';
import { cn } from '@/lib/cn';

export type { BreadcrumbItem };

export interface BreadcrumbsProps {
  /** Optional override; when omitted the trail comes from `useCrumbs`. */
  items?: BreadcrumbItem[];
  variant?: 'full' | 'compact';
  className?: string;
}

/**
 * Crumb link targets are computed strings; the registry guarantees they
 * exist in the route table, so this is the one sanctioned `as string` cast
 * for TSR's typed Link.
 */
function CrumbLink({
  item,
  className,
}: {
  item: BreadcrumbItem & { to: string };
  className?: string;
}): JSX.Element {
  return (
    <Link
      to={item.to as string}
      hash={item.hash}
      className={className}
      title={item.label}
    >
      {item.label}
    </Link>
  );
}

export function Breadcrumbs({
  items,
  variant = 'full',
  className,
}: BreadcrumbsProps): JSX.Element | null {
  const computed = useCrumbs();
  const crumbs = items ?? computed;

  // Nothing useful to show on the dashboard itself.
  if (crumbs.length <= 1) return null;

  if (variant === 'compact') {
    // Nearest linkable ancestor (usually the parent, sometimes further up
    // when intermediate crumbs are unroutable labels).
    const parent = [...crumbs.slice(0, -1)]
      .reverse()
      .find((c): c is BreadcrumbItem & { to: string } => !!c.to);
    if (!parent) return null;
    return (
      <nav aria-label="Breadcrumb" className={cn('flex items-center', className)}>
        <Link
          to={parent.to as string}
          hash={parent.hash}
          className="flex min-w-0 items-center gap-1 rounded px-1 text-sm text-muted-foreground transition-colors hover:text-foreground"
        >
          <ChevronLeft className="h-4 w-4 shrink-0" aria-hidden />
          <span className="max-w-[180px] truncate">{parent.label}</span>
        </Link>
      </nav>
    );
  }

  return (
    <nav
      aria-label="Breadcrumb"
      className={cn('flex items-center text-xs text-muted-foreground', className)}
    >
      <ol className="flex flex-wrap items-center gap-1.5">
        {crumbs.map((item, idx) => {
          const isLast = idx === crumbs.length - 1;
          return (
            <Fragment key={`${item.label}-${idx}`}>
              <li className="flex items-center">
                {idx === 0 ? (
                  <Home className="mr-1.5 h-3.5 w-3.5 opacity-60" aria-hidden />
                ) : null}
                {isLast || !item.to ? (
                  <span
                    aria-current={isLast ? 'page' : undefined}
                    className={cn(
                      'max-w-[220px] truncate',
                      isLast && 'font-medium text-foreground',
                    )}
                    title={item.label}
                  >
                    {item.label}
                  </span>
                ) : (
                  <CrumbLink
                    item={item as BreadcrumbItem & { to: string }}
                    className="max-w-[220px] truncate rounded px-1 transition-colors hover:text-foreground"
                  />
                )}
              </li>
              {!isLast ? (
                <li aria-hidden>
                  <ChevronRight className="h-3.5 w-3.5 opacity-50" />
                </li>
              ) : null}
            </Fragment>
          );
        })}
      </ol>
    </nav>
  );
}
